
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Contact } from "@/types/investor";
import { 
  getEmailDrafts, 
  deleteEmailDraft, 
  labelEmailDraft, 
  saveEmailDraft,
  EmailDraft 
} from "@/utils/investors/draftUtils";
import { generateEmailContent } from "@/utils/aiUtils";
import { getAPIKey } from "@/utils/ai/apiKeyUtils";
import { v4 as uuidv4 } from "uuid";
import { EmailRecord } from "@/types/email";
import { useEmailStorage } from "./useEmailStorage";
import { useGoogleAuth } from "@/contexts/GoogleAuthContext";

export const useDraftActions = (
  investorId: string,
  selectedContactData: Contact | null,
  setGeneratedEmail: (email: string) => void,
  isLoading: boolean,
  setIsLoading: (loading: boolean) => void,
  businessInfo: string
) => {
  const { supabaseSession } = useGoogleAuth();
  const [drafts, setDrafts] = useState<EmailDraft[]>([]);
  const [selectedDraftId, setSelectedDraftId] = useState<string | null>(null);
  const [showDrafts, setShowDrafts] = useState<boolean>(false);
  const { saveEmailRecord } = useEmailStorage();
  
  // Load drafts on component mount
  useEffect(() => {
    const loadDrafts = async () => {
      if (supabaseSession?.user?.id) {
        try {
          const loadedDrafts = await getEmailDrafts(supabaseSession.user.id, investorId);
          setDrafts(loadedDrafts);
        } catch (error) {
          console.error("Error loading drafts:", error);
        }
      }
    };
    
    loadDrafts();
  }, [investorId, supabaseSession?.user?.id]);
  
  const handleSaveAsDraft = async () => {
    if (!selectedContactData) {
      toast.error("Please select a contact");
      return;
    }
    
    if (!supabaseSession?.user?.id) {
      toast.error("You must be logged in to save drafts");
      return;
    }
    
    const currentEmail = setGeneratedEmail as unknown as { arguments: string[] };
    const generatedEmail = currentEmail.arguments ? currentEmail.arguments[0] : "";
    
    if (!generatedEmail.trim()) {
      toast.error("Nothing to save");
      return;
    }
    
    try {
      // Save to database using the draftUtils function
      const newDraft = await saveEmailDraft(
        supabaseSession.user.id,
        investorId,
        selectedContactData.id,
        generatedEmail
      );
      
      // Also save to emailRecords
      const draftRecord: EmailRecord = {
        id: newDraft.id,
        investorId: investorId,
        contactId: selectedContactData.id,
        subject: `Intro to ${selectedContactData.firmName || 'Company'}`,
        body: generatedEmail,
        sentAt: "",
        sentVia: null,
        hasResponse: false,
        status: "draft",
        label: newDraft.label || "Draft",
        createdAt: new Date()
      };
      
      await saveEmailRecord(draftRecord);
      
      // Update the drafts state with the new drafts
      const updatedDrafts = await getEmailDrafts(supabaseSession.user.id, investorId);
      setDrafts(updatedDrafts);
      setSelectedDraftId(newDraft.id);
      toast.success("Draft saved successfully");
    } catch (error) {
      console.error("Error saving draft:", error);
      toast.error("Failed to save draft");
    }
  };
  
  const handleSelectDraft = (draft: EmailDraft) => {
    setGeneratedEmail(draft.content);
    setSelectedDraftId(draft.id);
  };
  
  const handleDeleteDraft = async (draftId: string) => {
    if (!supabaseSession?.user?.id) {
      toast.error("You must be logged in to delete drafts");
      return;
    }
    
    try {
      await deleteEmailDraft(supabaseSession.user.id, draftId);
      
      // Update the drafts state with the new drafts
      const updatedDrafts = await getEmailDrafts(supabaseSession.user.id, investorId);
      setDrafts(updatedDrafts);
      
      if (selectedDraftId === draftId) {
        setSelectedDraftId(null);
      }
      
      toast.success("Draft deleted");
    } catch (error) {
      console.error("Error deleting draft:", error);
      toast.error("Failed to delete draft");
    }
  };
  
  const handleLabelDraft = async (draftId: string, label: string) => {
    if (!supabaseSession?.user?.id) {
      toast.error("You must be logged in to label drafts");
      return;
    }
    
    try {
      await labelEmailDraft(supabaseSession.user.id, draftId, label);
      
      // Update the drafts state with the new drafts
      const updatedDrafts = await getEmailDrafts(supabaseSession.user.id, investorId);
      setDrafts(updatedDrafts);
    } catch (error) {
      console.error("Error updating draft label:", error);
      toast.error("Failed to update label");
    }
  };
  
  const handleRegenerateDraft = async (draft: EmailDraft) => {
    if (!selectedContactData) {
      toast.error("Please select a contact");
      return;
    }
    
    if (!supabaseSession?.user?.id) {
      toast.error("You must be logged in to regenerate drafts");
      return;
    }
    
    try {
      // Check if we have OpenAI API key stored
      const openAiKey = getAPIKey("openai");
      
      if (!openAiKey) {
        toast.error("Please configure your OpenAI API key in the API Connections section");
        return;
      }
      
      setIsLoading(true);
      toast.info("Regenerating email with variations...");
      
      const prompt = `
Previous email draft: 
${draft.content}

Please create a new version of this email with improvements. 
You may change the tone, structure, or emphasis, but keep the core information and purpose intact.
Focus on emphasizing the company's traction metrics even more clearly.
`;
      
      const emailContent = await generateEmailContent(
        { id: investorId } as any,
        selectedContactData,
        businessInfo + "\n\n" + prompt,
        openAiKey
      );
      
      setGeneratedEmail(emailContent);
      
      // Save as a new draft
      const newDraftId = uuidv4();
      
      // Save to database using the draftUtils function
      const newDraft = await saveEmailDraft(
        supabaseSession.user.id,
        investorId,
        selectedContactData.id,
        emailContent,
        "Variation " + new Date().toLocaleTimeString()
      );
      
      // Also save to emailRecords
      const draftRecord: EmailRecord = {
        id: newDraft.id,
        investorId: investorId,
        contactId: selectedContactData.id,
        subject: `Intro to ${selectedContactData.firmName || 'Company'}`,
        body: emailContent,
        sentAt: "",
        sentVia: null,
        hasResponse: false,
        status: "draft",
        label: "Variation " + new Date().toLocaleTimeString(),
        createdAt: new Date()
      };
      
      await saveEmailRecord(draftRecord);
      
      // Update the drafts state with the new drafts
      const updatedDrafts = await getEmailDrafts(supabaseSession.user.id, investorId);
      setDrafts(updatedDrafts);
      setSelectedDraftId(newDraft.id);
      
      toast.success("Email regenerated with variations");
    } catch (error) {
      console.error("Error regenerating email:", error);
      toast.error("Failed to regenerate email");
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleDrafts = () => {
    setShowDrafts(!showDrafts);
  };

  return {
    drafts,
    selectedDraftId,
    showDrafts,
    handleSaveAsDraft,
    handleSelectDraft,
    handleDeleteDraft,
    handleLabelDraft,
    handleRegenerateDraft,
    handleToggleDrafts
  };
};
